import { useEffect, useId, useRef, useState } from 'react';
import { extractErrorMessage } from '../services/api';
import { flowkitSkillsApi, type FlowkitSkillsCatalog, type FlowkitSkillRecipe, type SkillDescriptor } from '../services/flowkitSkills';
import './FlowkitSkills.css';

const availabilityLabels: Record<SkillDescriptor['availability'], string> = {
  recipe: 'Recipe only',
  'approval-required': 'Blocked · approval required',
  unsupported: 'Blocked · unsupported',
  'setup-required': 'Blocked · setup required',
};
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
function validDescriptor(value: unknown): value is SkillDescriptor {
  const skill = value as SkillDescriptor | null;
  return Boolean(skill && ['id', 'title', 'description', 'category', 'effect', 'availability'].every(key =>
    typeof skill[key as keyof SkillDescriptor] === 'string')
    && Object.keys(availabilityLabels).includes(skill.availability)
    && ['read-only', 'generation', 'local-write', 'configuration', 'publishing', 'external'].includes(skill.effect)
    && strings(skill.prerequisites) && strings(skill.caveats) && typeof skill.source?.available === 'boolean'
    && typeof skill.source.relativePath === 'string'
    && (skill.source.sha256 === undefined || typeof skill.source.sha256 === 'string')
    && (skill.source.error === undefined || typeof skill.source.error === 'string'));
}

async function verifySource(recipe: FlowkitSkillRecipe): Promise<string> {
  const expected = recipe.skill.source.sha256;
  if (!expected || !/^[a-f0-9]{64}$/i.test(expected)) return 'No valid SHA-256 was reported; source integrity is unverified.';
  if (!globalThis.crypto?.subtle) return 'SHA-256 is server-reported only; local verification is unavailable in this browser context.';
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(recipe.content));
    const actual = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    return actual === expected.toLowerCase()
      ? 'Verified SHA-256: the full source body matches the reported hash. This does not make the recipe safe to execute.'
      : 'Source hash mismatch. The displayed body is unverified; do not rely on the reported hash.';
  } catch {
    return 'Local SHA-256 verification failed; the displayed source is unverified.';
  }
}

function RecipeBadges({ skill }: { skill: SkillDescriptor }) {
  return <span className="flowkit-skill-badges">
    <span className="flowkit-skill-badge">{availabilityLabels[skill.availability] ?? skill.availability}</span>
    {!skill.source.available && <span className="flowkit-skill-badge">Source unavailable</span>}
  </span>;
}

function RecipeMetadata({ skill }: { skill: SkillDescriptor }) {
  return <div className="flowkit-skill-metadata">
    <h3>{skill.title}</h3>
    <p><code>{skill.id}</code> · {skill.category} · Effect: {skill.effect}</p>
    <RecipeBadges skill={skill} />
    <p>{skill.description}</p>
    <dl>
      <dt>Source path</dt><dd><code>{skill.source.relativePath}</code></dd>
      <dt>Reported SHA-256</dt><dd><code>{skill.source.sha256 || 'Unavailable — no verified hash'}</code></dd>
    </dl>
    {skill.source.error && <p className="flowkit-skill-warning">{skill.source.error}</p>}
    <h4>Manual prerequisites</h4>
    {skill.prerequisites.length ? <ul>{skill.prerequisites.map((item, index) => <li key={index}>{item}</li>)}</ul>
      : <p>None listed. This browser still cannot execute recipes.</p>}
    {skill.caveats.length > 0 && <><h4>Recipe caveats</h4>
      <ul>{skill.caveats.map((item, index) => <li key={index}>{item}</li>)}</ul></>}
  </div>;
}

export default function FlowkitSkillsPanel({ engine }: { engine?: string } = {}) {
  void engine;
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<FlowkitSkillsCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState<SkillDescriptor | null>(null);
  const [recipe, setRecipe] = useState<FlowkitSkillRecipe | null>(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeError, setRecipeError] = useState('');
  const [integrity, setIntegrity] = useState('');
  const catalogRequest = useRef<AbortController | null>(null);
  const recipeRequest = useRef<AbortController | null>(null);
  const detailId = useId();

  // Ignore stale replies as well as aborting I/O (including after unmount).
  // https://react.dev/reference/react/useEffect#fetching-data-with-effects
  useEffect(() => () => {
    catalogRequest.current?.abort();
    recipeRequest.current?.abort();
  }, []);

  const loadCatalog = async () => {
    catalogRequest.current?.abort();
    const controller = new AbortController();
    catalogRequest.current = controller;
    setCatalogLoading(true);
    setCatalogError('');
    try {
      const data = await flowkitSkillsApi.list(controller.signal);
      if (!data || !Array.isArray(data.skills) || !data.skills.every(validDescriptor) || !strings(data.warnings)) {
        throw new Error('Invalid skill catalog response.');
      }
      if (!controller.signal.aborted) setCatalog(data);
    } catch (cause) {
      if (!controller.signal.aborted) setCatalogError(extractErrorMessage(cause, 'Could not read the skill catalog.'));
    } finally {
      if (!controller.signal.aborted) setCatalogLoading(false);
      if (catalogRequest.current === controller) catalogRequest.current = null;
    }
  };

  const selectRecipe = async (skill: SkillDescriptor) => {
    recipeRequest.current?.abort();
    recipeRequest.current = null;
    setSelected(skill);
    setRecipe(null);
    setRecipeError('');
    setIntegrity('');
    setRecipeLoading(false);
    if (!skill.source.available) {
      setRecipeError(skill.source.error || 'Source unavailable. Metadata is shown, but the full body cannot be read.');
      return;
    }
    const controller = new AbortController();
    recipeRequest.current = controller;
    setRecipeLoading(true);
    try {
      const data = await flowkitSkillsApi.recipe(skill.id, controller.signal);
      if (!data || !validDescriptor(data.skill) || data.skill.id !== skill.id
          || typeof data.content !== 'string' || !strings(data.ddlabCaveats)) throw new Error('Invalid full recipe response.');
      if (!data.skill.source.available) throw new Error(data.skill.source.error || 'Recipe source is no longer available.');
      if (controller.signal.aborted) return;
      const verification = await verifySource(data);
      if (!controller.signal.aborted) { setRecipe(data); setIntegrity(verification); }
    } catch (cause) {
      if (!controller.signal.aborted) setRecipeError(extractErrorMessage(cause, 'Could not read the full recipe.'));
    } finally {
      if (!controller.signal.aborted) setRecipeLoading(false);
      if (recipeRequest.current === controller) recipeRequest.current = null;
    }
  };

  const toggle = (expanded: boolean) => {
    setOpen(expanded);
    if (expanded) {
      if (!catalog && !catalogRequest.current) void loadCatalog();
    } else {
      catalogRequest.current?.abort();
      catalogRequest.current = null;
      setCatalogLoading(false);
      if (recipeRequest.current) {
        recipeRequest.current.abort();
        recipeRequest.current = null;
        setRecipeLoading(false);
        setRecipeError('Reading cancelled. Select a recipe again to read its full source.');
      }
    }
  };

  const needle = query.trim().toLowerCase();
  const visible = (catalog?.skills ?? []).filter(skill => (!category || skill.category === category)
    && [skill.id, skill.title, skill.description, skill.category].some(text => text.toLowerCase().includes(needle)));
  const categories = [...new Set(catalog?.skills.map(skill => skill.category) ?? [])].sort();

  return (
    <details className="flowkit-skills-panel" onToggle={event => toggle(event.currentTarget.open)}>
      <summary><strong>Flowkit skills</strong><span>{catalog ? `${catalog.skills.length} recipes · Read-only` : 'Read-only recipes'}</span></summary>
      {open && <div className="flowkit-skills-content">
        <p>Browse original recipes as text only. Reading does not load them into chat or execute them. Workflow badges describe manual prerequisites, not permission to run.</p>
        {catalogLoading && <p role="status">Reading skill catalog…</p>}
        {catalogError && <div className="flowkit-skill-warning" role="alert"><p>{catalogError}</p>
          <button type="button" className="studio-button-quiet" onClick={() => void loadCatalog()} disabled={catalogLoading}>Retry catalog</button></div>}
        {catalog && <>
          <button type="button" className="studio-button-quiet" onClick={() => void loadCatalog()} disabled={catalogLoading}>Refresh catalog</button>
          {catalog.warnings.length > 0 && <div className="flowkit-skill-warning" role="note"><strong>Catalog warnings</strong>
            <ul>{catalog.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
          <div className="flowkit-skills-filters">
            <label>Search recipes<input type="search" className="studio-input" value={query} onChange={event => setQuery(event.target.value)} placeholder="Name, ID, or description" /></label>
            <label>Category<select className="studio-input" value={category} onChange={event => setCategory(event.target.value)}>
              <option value="">All categories</option>{categories.map(item => <option key={item} value={item}>{item}</option>)}
            </select></label>
          </div>
          <p role="status">{visible.length} of {catalog.skills.length} recipes shown</p>
          <ul className="flowkit-skills-list" aria-label="Flowkit recipes">
            {visible.map(skill => <li key={skill.id}>
              <button type="button" className="flowkit-skill-choice" aria-pressed={selected?.id === skill.id} aria-controls={detailId}
                aria-label={`View recipe ${skill.id}: ${skill.title}`} onClick={() => void selectRecipe(skill)}>
                <strong>{skill.title}</strong> <code>{skill.id}</code>
                <RecipeBadges skill={skill} />
                <span>{skill.description}</span>
                <small>{skill.category} · Effect: {skill.effect}</small>
              </button>
            </li>)}
          </ul>
          {visible.length === 0 && <p>{catalog.skills.length ? 'No recipes match these filters.' : 'No recipes were returned by the catalog.'}</p>}
        </>}
        <section id={detailId} className="flowkit-skill-detail" aria-label="Selected recipe" aria-busy={recipeLoading}>
          {selected ? <>
            <RecipeMetadata skill={recipe?.skill ?? selected} />
            {recipeLoading && <p role="status">Reading the whole recipe and checking its hash…</p>}
            {recipeError && <div className="flowkit-skill-warning" role="alert"><p>{recipeError}</p>
              {selected.source.available && <button type="button" className="studio-button-quiet" onClick={() => void selectRecipe(selected)}>Read recipe again</button>}
            </div>}
            {recipe && <>
              <p className="flowkit-skill-notice" role="note">{integrity}</p>
              {selected.source.sha256 !== recipe.skill.source.sha256 && <p className="flowkit-skill-warning" role="note">Source changed since the catalog was read. Metadata above describes this full-body response.</p>}
              <h4>DDlab caveats</h4>
              {recipe.ddlabCaveats.length ? <ul>{recipe.ddlabCaveats.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>None reported.</p>}
              <p>Whole source · Untrusted text · Read-only</p>
              <pre className="flowkit-skill-source" tabIndex={0} aria-label={`${recipe.skill.id} full recipe source`}>{recipe.content}</pre>
            </>}
          </> : <p>Select a recipe to read its full source. Blocked workflows are still readable when their source is available.</p>}
        </section>
      </div>}
    </details>
  );
}